import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, DatePicker, Empty, Space, Switch, Table, Tag, Tooltip, theme } from 'antd';
import { InfoCircleOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import { PageHeader } from 'components/headings';
import { useAppApi } from 'hooks/useAppApi';
import { useTenantFlags } from 'hooks/useTenantFlags';
import ProLockOverlay from 'components/global/ProLock/ProLockOverlay';

const { RangePicker } = DatePicker;
const { useToken } = theme;

const PRESETS = [
  { label: '3 meses', value: () => [dayjs().subtract(3, 'month'), dayjs()] },
  { label: '6 meses', value: () => [dayjs().subtract(6, 'month'), dayjs()] },
  { label: '12 meses', value: () => [dayjs().subtract(12, 'month'), dayjs()] },
];

const pctColor = (v) => {
  if (v >= 30) return 'green';
  if (v >= 20) return 'orange';
  return 'red';
};

const fmtMttr = (v, unit) => (v == null ? '—' : `${v} ${unit}`);

const identity = (x) => x;

const titleWithTip = (label, tip) => (
  <Space size={4}>
    {label}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
    </Tooltip>
  </Space>
);

function TecnicosPage() {
  const { token } = useToken();
  const { isPro } = useTenantFlags();
  const [rango, setRango] = useState(() => [dayjs().subtract(12, 'month'), dayjs()]);
  const [mostrarTodos, setMostrarTodos] = useState(false);

  const { data, isLoading, error, call } = useAppApi({
    baseUrl: '/kpi/tecnicos',
    mapResults: identity,
  });

  const desde = rango[0].format('YYYY-MM-DD');
  const hasta = rango[1].format('YYYY-MM-DD');

  useEffect(() => {
    if (!isPro) return;
    call({ queryParams: { desde, hasta } });
  }, [desde, hasta, call, isPro]);

  const filas = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return mostrarTodos ? data : data.filter((t) => t.total_actividades >= 3);
  }, [data, mostrarTodos]);

  const columns = useMemo(
    () => [
      {
        title: 'Técnico',
        dataIndex: 'nombre',
        key: 'nombre',
        fixed: 'left',
        ellipsis: true,
        sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      },
      {
        title: titleWithTip(
          'Mant. FIN',
          'Cantidad de mantenciones preventivas cerradas por el técnico dentro del período. Incluye FINALIZADA (firmada por el técnico) y ENVIADA (ya despachada al cliente).'
        ),
        dataIndex: 'mantenciones_fin',
        key: 'mantenciones_fin',
        width: 110,
        align: 'right',
        sorter: (a, b) => a.mantenciones_fin - b.mantenciones_fin,
      },
      {
        title: titleWithTip(
          'MTTR mant.',
          'Tiempo promedio (en horas) entre el inicio de la mantención en terreno y su firma de cierre. "—" cuando no hay mantenciones finalizadas en el período.'
        ),
        dataIndex: 'mttr_mantencion_horas',
        key: 'mttr_mantencion_horas',
        width: 130,
        align: 'right',
        render: (v) => fmtMttr(v, 'h'),
        sorter: (a, b) =>
          (a.mttr_mantencion_horas ?? Infinity) - (b.mttr_mantencion_horas ?? Infinity),
      },
      {
        title: titleWithTip(
          'ODT FIN',
          'Cantidad de órdenes de trabajo (ODT) que el técnico firmó y completó dentro del período.'
        ),
        dataIndex: 'odt_fin',
        key: 'odt_fin',
        width: 100,
        align: 'right',
        sorter: (a, b) => a.odt_fin - b.odt_fin,
      },
      {
        title: titleWithTip(
          'MTTR ODT',
          'Tiempo promedio (en días) desde que se crea la ODT hasta que el técnico la firma. Incluye el tiempo de espera entre asignación y ejecución.'
        ),
        dataIndex: 'mttr_odt_dias',
        key: 'mttr_odt_dias',
        width: 120,
        align: 'right',
        render: (v) => fmtMttr(v, 'd'),
        sorter: (a, b) => (a.mttr_odt_dias ?? Infinity) - (b.mttr_odt_dias ?? Infinity),
      },
      {
        title: titleWithTip(
          'Emerg.',
          'Cantidad de comprobantes de emergencia atendidos por el técnico en el período.'
        ),
        dataIndex: 'emergencias',
        key: 'emergencias',
        width: 100,
        align: 'right',
        sorter: (a, b) => a.emergencias - b.emergencias,
      },
      {
        title: titleWithTip(
          'MTTR emerg. (h)',
          'Tiempo promedio en horas entre started_at y completed_at del comprobante. Null si el comprobante no registró timestamps completos (vouchers anteriores a la migración DATETIME).'
        ),
        dataIndex: 'mttr_emergencia_horas',
        key: 'mttr_emergencia_horas',
        width: 140,
        align: 'right',
        render: (v) => (v == null ? '—' : v.toFixed(1)),
        sorter: (a, b) =>
          (a.mttr_emergencia_horas ?? Infinity) - (b.mttr_emergencia_horas ?? Infinity),
      },
      {
        title: titleWithTip(
          'Sol. asig.',
          'Solicitudes ejecutivo asignadas al técnico (creadas dentro del período), sin importar su estado actual.'
        ),
        dataIndex: 'sol_asignadas',
        key: 'sol_asignadas',
        width: 110,
        align: 'right',
        sorter: (a, b) => a.sol_asignadas - b.sol_asignadas,
      },
      {
        title: titleWithTip(
          'Sol. cerr.',
          'Solicitudes ejecutivo del período cerradas por el técnico (estado FINALIZADA o ENVIADA).'
        ),
        dataIndex: 'sol_cerradas',
        key: 'sol_cerradas',
        width: 110,
        align: 'right',
        sorter: (a, b) => a.sol_cerradas - b.sol_cerradas,
      },
      {
        title: titleWithTip(
          '% Cierre',
          'Porcentaje de solicitudes cerradas sobre asignadas en el período. Verde ≥30% · Naranja 20-29% · Rojo <20%.'
        ),
        dataIndex: 'pct_cierre',
        key: 'pct_cierre',
        width: 120,
        align: 'right',
        render: (v) => <Tag color={pctColor(v)}>{v}%</Tag>,
        sorter: (a, b) => a.pct_cierre - b.pct_cierre,
      },
      {
        title: titleWithTip(
          'Carga viva',
          'Solicitudes que el técnico tiene AHORA en estado ASIGNADA o EN_CURSO. No depende del rango seleccionado — refleja el estado actual.'
        ),
        dataIndex: 'carga_viva',
        key: 'carga_viva',
        width: 120,
        align: 'right',
        sorter: (a, b) => a.carga_viva - b.carga_viva,
      },
    ],
    []
  );

  return (
    <ProLockOverlay
      locked={!isPro}
      title="Ranking de técnicos"
      description="Comparativa de desempeño de tu equipo técnico con métricas cruzadas. Disponible en Pro."
    >
      <div style={{ padding: token.paddingLG, paddingBottom: token.paddingXL }}>
        <PageHeader
          icon={TeamOutlined}
          title="Técnicos"
          subtitle="Ranking comparativo de desempeño por período"
        />

        {error && (
          <Alert
            type="error"
            message="No se pudo cargar el ranking de técnicos"
            showIcon
            style={{ marginBottom: token.marginLG }}
          />
        )}

        <Alert
          type="info"
          showIcon
          message="Cómo leer esta tabla"
          description={
            <span>
              Cada fila es un técnico activo del tenant. Las métricas cruzan cuatro fuentes:
              mantenciones preventivas, órdenes de trabajo (ODT), emergencias y solicitudes
              ejecutivo. Pasá el cursor por el ícono <InfoCircleOutlined /> de cada columna para ver
              su definición exacta. <strong>MTTR</strong> = tiempo medio de resolución.
            </span>
          }
          style={{ marginBottom: token.marginLG }}
        />

        <Card>
          <Space style={{ marginBottom: token.marginMD }} wrap size={[16, 8]}>
            <RangePicker
              value={rango}
              onChange={(v) => {
                if (v && v[0] && v[1]) setRango(v);
              }}
              presets={PRESETS.map((p) => ({ label: p.label, value: p.value() }))}
              allowClear={false}
            />
            <Space size={8}>
              <span>Mostrar técnicos con poca actividad</span>
              <Switch checked={mostrarTodos} onChange={setMostrarTodos} />
            </Space>
          </Space>

          {filas.length === 0 && !isLoading ? (
            <Empty description="Sin técnicos con actividad en el período" />
          ) : (
            <Table
              dataSource={filas}
              columns={columns}
              rowKey="rut"
              loading={isLoading}
              pagination={{ pageSize: 25, showSizeChanger: false }}
              scroll={{ x: 1100 }}
              size="middle"
            />
          )}

          <div style={{ color: token.colorTextTertiary, fontSize: 12, marginTop: token.marginSM }}>
            {mostrarTodos
              ? 'Mostrando todos los técnicos activos del tenant'
              : 'Solo técnicos con ≥3 actividades en el período seleccionado'}
          </div>
        </Card>
      </div>
    </ProLockOverlay>
  );
}

export default TecnicosPage;
